<template>
  <div class="UIContentSwitcher">
    <ul :class="['UIContentSwitcher__list', $attrs.class]">
      <li v-for="item in items" :key="item.id">
        <UIContentSwitcherButton
          class="UIContentSwitcher__button"
          @click="() => selectItem(item)"
          :isActive="item.id === selectedItem"
          :isDisabled="item.isDisabled"
        >
          {{ item.label }}
        </UIContentSwitcherButton>
      </li>
    </ul>
  </div>

  <slot name="before" />
  <slot>
    <template v-for="item in items" :key="item.id">
      <slot
        v-if="useShow || item.id === selectedItem"
        :name="`container-${item.id}`"
      >
        <UIContentSwitcherContent
          :show="!useShow || item.id === selectedItem"
          :key="item.id"
        >
          <slot :name="item.id" />
        </UIContentSwitcherContent>
      </slot>
    </template>
  </slot>
  <slot name="after" />
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { IUIContentSwitcherItem } from './types'
import UIContentSwitcherButton from './UIContentSwitcherButton.vue'
import UIContentSwitcherContent from './UIContentSwitcherContent.vue'

const props = withDefaults(
  defineProps<{
    items: IUIContentSwitcherItem[]
    isDisabled?: boolean
    useShow?: boolean
    defaultItemKey?: string
  }>(),
  {},
)
const emit = defineEmits(['onItemSelect'])

const selectedItem = ref('')

const selectItem = (item: IUIContentSwitcherItem) => {
  if (!item.isDisabled) {
    selectedItem.value = item.id
  }
}

onMounted(() => {
  const defaultItem = props.items[0]
  selectedItem.value = props.defaultItemKey || defaultItem?.id
})

watch(selectedItem, (newItemSelected) => emit('onItemSelect', newItemSelected))

watch(
  () => props.defaultItemKey,
  (newDefaultItemKey) => {
    if (newDefaultItemKey) {
      selectedItem.value = newDefaultItemKey
    }
  },
)

defineExpose({
  selectItem: (itemId: string) => {
    selectedItem.value = itemId
  },
})
</script>

<style scoped lang="scss">
.UIContentSwitcher {
  display: flex;

  .UIContentSwitcher__list {
    @include customScrollbar;
    background: $color-ui-gray-cool-50;
    border: solid 1px $color-ui-gray-cool-200;

    font-family: $font-inter;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    box-sizing: border-box;
    border-radius: 0.5rem;
    gap: var(--gap, 0rem);
    align-items: center;
    margin-bottom: 1.5rem;

    li {
      list-style: none;
    }
  }
}
</style>
